import { PortalData, SectionId } from '../../types/portal';
import { 
  Newspaper, 
  FileText, 
  Phone, 
  Warning, 
  Calendar, 
  Images,
  EnvelopeSimple,
  MapPin,
  ArrowRight,
  Download
} from '@phosphor-icons/react';

interface MinimalistTemplateProps {
  data: PortalData;
}

// Section components with minimalist design
const NewsSection = ({ news, colors }: { news: PortalData['news']; colors: PortalData['settings']['colors'] }) => {
  if (!news || news.length === 0) return null;
  
  return (
    <section className="py-16 border-b border-slate-100" id="news">
      <h2 
        className="text-xs font-semibold tracking-widest uppercase mb-8"
        style={{ color: colors.primary }}
      >
        Nyheter
      </h2>
      <div className="space-y-12">
        {news.slice(0, 5).map((item) => (
          <article key={item.id} className="group">
            <time className="text-sm text-slate-400 block mb-2">
              {new Date(item.published_at).toLocaleDateString('sv-SE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            <h3 
              className="text-2xl font-light mb-3 group-hover:opacity-70 transition-opacity"
              style={{ color: colors.text }}
            >
              {item.title}
            </h3>
            <p className="text-slate-500 leading-relaxed max-w-2xl">
              {item.content.replace(/<[^>]*>/g, '').substring(0, 300)}...
            </p>
          </article>
        ))}
      </div>
    </section>
  );
};

const DocumentsSection = ({ documents, colors }: { documents: PortalData['documents']; colors: PortalData['settings']['colors'] }) => {
  if (!documents || documents.length === 0) return null;
  
  return (
    <section className="py-16 border-b border-slate-100" id="documents">
      <h2 
        className="text-xs font-semibold tracking-widest uppercase mb-8"
        style={{ color: colors.primary }}
      >
        Dokument
      </h2>
      <div className="space-y-3">
        {documents.map((doc) => (
          <a 
            key={doc.id}
            href={doc.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between py-4 border-b border-slate-100 hover:border-slate-300 transition-colors group"
          >
            <div>
              <p className="font-medium" style={{ color: colors.text }}>{doc.name}</p>
              <p className="text-sm text-slate-400 mt-1">
                {doc.category} • {new Date(doc.uploaded_at).toLocaleDateString('sv-SE')}
              </p>
            </div>
            <Download 
              size={18} 
              className="text-slate-300 group-hover:text-slate-600 transition-colors"
            />
          </a>
        ))}
      </div>
    </section>
  );
};

const ContactSection = ({ contact, organization, colors }: { 
  contact: PortalData['contact']; 
  organization: PortalData['organization'];
  colors: PortalData['settings']['colors'];
}) => {
  return (
    <section className="py-16 border-b border-slate-100" id="contact">
      <h2 
        className="text-xs font-semibold tracking-widest uppercase mb-8"
        style={{ color: colors.primary }}
      >
        Kontakt
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
          {contact.board_email && (
            <div>
              <p className="text-sm text-slate-400 mb-1">E-post</p>
              <a 
                href={`mailto:${contact.board_email}`}
                className="text-lg hover:opacity-70 transition-opacity"
                style={{ color: colors.text }}
              >
                {contact.board_email}
              </a>
            </div>
          )}
          {contact.board_phone && (
            <div>
              <p className="text-sm text-slate-400 mb-1">Telefon</p>
              <a 
                href={`tel:${contact.board_phone}`}
                className="text-lg hover:opacity-70 transition-opacity"
                style={{ color: colors.text }}
              >
                {contact.board_phone}
              </a>
            </div>
          )}
          {contact.address && (
            <div>
              <p className="text-sm text-slate-400 mb-1">Adress</p>
              <p className="text-lg" style={{ color: colors.text }}>{contact.address}</p>
            </div>
          )}
        </div>
        
        {contact.board_members && contact.board_members.length > 0 && (
          <div>
            <p className="text-sm text-slate-400 mb-4">Styrelse</p>
            <div className="space-y-4">
              {contact.board_members.map((member, idx) => (
                <div key={idx}>
                  <p className="font-medium" style={{ color: colors.text }}>{member.name}</p>
                  <p className="text-sm text-slate-400">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const IssuesSection = ({ organization, colors }: { 
  organization: PortalData['organization'];
  colors: PortalData['settings']['colors'];
}) => {
  return (
    <section className="py-16 border-b border-slate-100" id="issues">
      <h2 
        className="text-xs font-semibold tracking-widest uppercase mb-8"
        style={{ color: colors.primary }}
      >
        Felanmälan
      </h2>
      <p className="text-lg text-slate-500 mb-8 max-w-xl">
        Har du upptäckt ett fel i fastigheten? Rapportera det enkelt via vårt formulär.
      </p>
      <a 
        href={`/report?org=${organization.slug}`}
        className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-70 transition-opacity"
        style={{ color: colors.primary }}
      >
        Anmäl fel <ArrowRight size={20} />
      </a>
    </section>
  );
};

const CalendarSection = ({ calendar, colors }: { calendar: PortalData['calendar']; colors: PortalData['settings']['colors'] }) => {
  if (!calendar || calendar.length === 0) return null;

  const upcomingEvents = calendar
    .filter(event => new Date(event.start_date) >= new Date())
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
    .slice(0, 5);

  if (upcomingEvents.length === 0) return null;

  return (
    <section className="py-16 border-b border-slate-100" id="calendar">
      <h2 
        className="text-xs font-semibold tracking-widest uppercase mb-8"
        style={{ color: colors.primary }}
      >
        Kalender
      </h2>
      <div className="space-y-8">
        {upcomingEvents.map((event) => {
          const date = new Date(event.start_date);
          return (
            <div key={event.id} className="flex gap-6">
              <div className="text-right w-20 flex-shrink-0">
                <p className="text-3xl font-light" style={{ color: colors.text }}>
                  {date.getDate()}
                </p>
                <p className="text-sm text-slate-400 uppercase">
                  {date.toLocaleDateString('sv-SE', { month: 'short' })}
                </p>
              </div>
              <div className="border-l border-slate-200 pl-6">
                <h3 className="text-lg font-medium" style={{ color: colors.text }}>
                  {event.title}
                </h3>
                {event.description && (
                  <p className="text-slate-500 mt-1">{event.description}</p>
                )}
                <p className="text-sm text-slate-400 mt-2">
                  {date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                  {event.location && ` • ${event.location}`}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const GallerySection = ({ gallery, colors }: { gallery: PortalData['gallery']; colors: PortalData['settings']['colors'] }) => {
  if (!gallery || gallery.length === 0) return null;

  return (
    <section className="py-16" id="gallery">
      <h2 
        className="text-xs font-semibold tracking-widest uppercase mb-8"
        style={{ color: colors.primary }}
      >
        Galleri
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {gallery.slice(0, 8).map((image) => (
          <div 
            key={image.id}
            className="aspect-square overflow-hidden cursor-pointer"
          >
            <img 
              loading="lazy"
              src={image.thumbnail_url || image.url}
              alt={image.caption || ''}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        ))}
      </div>
    </section>
  );
};

// Section renderer map
const SECTION_COMPONENTS: Record<SectionId, React.FC<{ data: PortalData }>> = {
  news: ({ data }) => <NewsSection news={data.news} colors={data.settings.colors} />,
  documents: ({ data }) => <DocumentsSection documents={data.documents} colors={data.settings.colors} />,
  contact: ({ data }) => <ContactSection contact={data.contact} organization={data.organization} colors={data.settings.colors} />,
  issues: ({ data }) => <IssuesSection organization={data.organization} colors={data.settings.colors} />,
  calendar: ({ data }) => <CalendarSection calendar={data.calendar} colors={data.settings.colors} />,
  gallery: ({ data }) => <GallerySection gallery={data.gallery} colors={data.settings.colors} />
};

export default function MinimalistTemplate({ data }: MinimalistTemplateProps) {
  const { settings, organization, news, documents, calendar, gallery, contact } = data;
  const { colors, visible_sections } = settings;

  // Define preferred section order
  const sectionOrder: SectionId[] = ['news', 'contact', 'calendar', 'issues', 'documents', 'gallery'];

  // Filter visible_sections to only include sections with content
  const sectionsWithContent = visible_sections.filter(sectionId => {
    switch (sectionId) {
      case 'news':
        return news && news.length > 0;
      case 'documents':
        return documents && documents.length > 0;
      case 'calendar':
        return calendar && calendar.length > 0;
      case 'gallery':
        return gallery && gallery.length > 0;
      case 'contact':
        return contact?.board_email || contact?.board_phone || contact?.address;
      case 'issues':
        return true;
      default:
        return true;
    }
  }).sort((a, b) => sectionOrder.indexOf(a) - sectionOrder.indexOf(b));

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: colors.background || '#ffffff' }}
    >
      {/* Header - very minimal */}
      <header className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            {settings.logo_url && (
              <img 
                src={settings.logo_url}
                alt={organization.name}
                className="w-12 h-12 object-contain"
              />
            )}
          </div>
          <h1 
            className="text-4xl md:text-5xl font-light tracking-tight"
            style={{ color: colors.text }}
          >
            {organization.name}
          </h1>
          {settings.meta_description && (
            <p className="text-xl text-slate-400 mt-4 font-light">
              {settings.meta_description}
            </p>
          )}
        </div>
      </header>

      {/* Navigation - horizontal, subtle */}
      <nav className="border-y border-slate-100">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex gap-8 overflow-x-auto py-4 scrollbar-hide">
            {sectionsWithContent.map(sectionId => {
              const names: Record<SectionId, string> = {
                news: 'Nyheter',
                documents: 'Dokument',
                contact: 'Kontakt',
                issues: 'Felanmälan',
                calendar: 'Kalender',
                gallery: 'Galleri'
              };
              
              return (
                <a
                  key={sectionId}
                  href={`#${sectionId}`}
                  className="text-sm tracking-wide whitespace-nowrap hover:opacity-70 transition-opacity"
                  style={{ color: colors.text }}
                >
                  {names[sectionId]}
                </a>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Sections */}
      <main className="max-w-3xl mx-auto px-6">
        {sectionsWithContent.map(sectionId => {
          const SectionComponent = SECTION_COMPONENTS[sectionId];
          if (!SectionComponent) return null;
          return <SectionComponent key={sectionId} data={data} />;
        })}
      </main>

      {/* Footer - minimal */}
      <footer className="py-12 mt-12">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-sm text-slate-400">
            {settings.footer_text || `© ${new Date().getFullYear()} ${organization.name}`}
          </p>
        </div>
      </footer>
    </div>
  );
}
